import { classNames } from '@framework/utils';
import { useGroupLicensePermissions } from '@group/License';
import type { ViewModelOperationLogSender } from '@model-framework/action-log';
import type { ViewId } from 'balus-common';
import { useGetTransformedVisibleAreaCenterPoint } from '@user/pages/ViewModelPage';
import { useCurrentUserPublicProfile } from '@user/PublicProfile/useUserPublicProfile';
import type { Rect } from '@view-model/models/common/basic/Rect';
import { ForeignObjectAutosize2 } from '@view-model/models/common/components/ForeignObjectAutosize';
import type { SizeType } from '@view-model/models/common/types/ui';
import { useStickyModelContentsOperation } from '@view-model/ui/components/Model';
import { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-hot-toast';
import { Trans, useTranslation } from 'react-i18next';

export type DragFileTarget = {
  viewId: ViewId;
};

export const initialDragFileTarget: DragFileTarget = {
  viewId: '',
};

type EnterAction = {
  type: 'Enter';
  payload: {
    viewId: ViewId;
  };
};
type ExitAction = {
  type: 'Exit';
  payload: {
    viewId: ViewId;
  };
};

export type DragFileTargetActions = EnterAction | ExitAction;

export const dragFileTargetReducer = (state: DragFileTarget, action: DragFileTargetActions): DragFileTarget => {
  switch (action.type) {
    case 'Enter':
      if (state.viewId === action.payload.viewId) {
        // 同じならviewIdを初期化する
        // （同一View内で、一度DnDエリアが表示され、そのDnDエリアから抜ける際にDragEnterが発火した場合）
        return {
          ...state,
          viewId: '',
        };
      }

      // 新しいviewIdをセット
      return {
        ...state,
        viewId: action.payload.viewId,
      };
    case 'Exit':
      if (state.viewId !== '' && action.payload.viewId !== '' && state.viewId !== action.payload.viewId) {
        // 異なるviewIdなら何もしない
        // （既に別のViewのDnDエリア内に留まっている状態 ＝＞
        //   既に別のViewのDragEnterが発火した後に元のViewのDragLeaveが発火した場合）
        return state;
      }

      return {
        ...state,
        viewId: '',
      };
    default:
      return state;
  }
};

type Props = {
  viewId: ViewId;
  viewRect: Rect;
  size: SizeType;
  fontSize: number;
  dispatch: React.Dispatch<DragFileTargetActions>;
  logSender: ViewModelOperationLogSender;
};

export const DragFileArea: React.FC<Props> = (props: Props) => {
  const { viewId, viewRect, size, fontSize, dispatch, logSender } = props;

  const { t } = useTranslation();
  const operation = useStickyModelContentsOperation();
  const userProfile = useCurrentUserPublicProfile();
  const getTransformedVisibleAreaCenterPoint = useGetTransformedVisibleAreaCenterPoint();
  const licensePermissions = useGroupLicensePermissions();
  const canUploadFile = licensePermissions?.fileUpload !== false;

  const onDrop = useCallback(
    async (files: File[]) => {
      if (!userProfile) return;

      if (!canUploadFile) {
        dispatch({ type: 'Exit', payload: { viewId } });
        toast.error(t('common.label.licenseRequired'));
        return;
      }

      const createPoint = operation.getCreatePoint(getTransformedVisibleAreaCenterPoint(viewRect));
      dispatch({ type: 'Exit', payload: { viewId } });
      const errorMessage = await operation.uploadAndAddFileContainers(files, createPoint, (fileContainerId) => {
        logSender('sticky:file_container:create', { fileContainerId, cause: 'drop' });
      });
      if (errorMessage) toast.error(errorMessage);
    },
    [
      canUploadFile,
      dispatch,
      getTransformedVisibleAreaCenterPoint,
      logSender,
      operation,
      t,
      userProfile,
      viewId,
      viewRect,
    ]
  );

  const onDragLeave = () => {
    dispatch({ type: 'Exit', payload: { viewId } });
  };

  const onClose = () => {
    dispatch({ type: 'Exit', payload: { viewId } });
  };

  const { getRootProps, getInputProps } = useDropzone({ onDrop, onDragLeave, noClick: true, noKeyboard: true });

  return (
    <ForeignObjectAutosize2>
      <div className="m-10" style={{ width: `${size.width - 80}px`, height: `${size.height - 80}px` }}>
        <div
          {...getRootProps()}
          className={classNames(
            'h-full w-full cursor-pointer',
            'flex items-center justify-center',
            'border border-dashed border-black bg-gray-200',
            'rounded-[40px]'
          )}
          onClick={onClose}
        >
          <div style={{ fontSize: fontSize * 0.6 }}>
            {canUploadFile ? (
              <Trans i18nKey="canvas.dragFile.hint" components={{ lineBreak: <br /> }} />
            ) : (
              t('common.label.licenseRequired')
            )}
          </div>
          <input type="file" {...getInputProps()} />
        </div>
      </div>
    </ForeignObjectAutosize2>
  );
};
